/**
 * React Hook for Authentication - Phase 1
 * 
 * Access user information and authentication state from Cognito.
 */

'use client';

import { useState, useEffect, useCallback } from 'react';
import { Hub } from 'aws-amplify/utils';
import { getUserInfo } from './auth-utils';
import type { CognitoSessionInfo } from './types';

export function useAuth() {
  const [userInfo, setUserInfo] = useState<CognitoSessionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadUserInfo = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const info = await getUserInfo();
      setUserInfo(info);
    } catch (err) {
      setError(err as Error);
      setUserInfo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserInfo();

    // Listen for auth state changes
    const unsubscribe = Hub.listen('auth', (data) => {
      switch (data.payload.event) {
        case 'signedIn':
        case 'signedOut':
        case 'tokenRefresh':
          loadUserInfo();
          break;
      }
    });

    return unsubscribe;
  }, [loadUserInfo]);

  return {
    userInfo,
    loading,
    error,
    isAuthenticated: !!userInfo,
    refresh: loadUserInfo,
  };
}
